import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkIcon from "@mui/icons-material/Link";

interface SocialShareProps {
  url: string;
  title: string;
  variant?: "desktop" | "mobile";
}

export const handleShare = (platform: string, url: string, title: string) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  let shareUrl = "";

  switch (platform) {
    case "linkedin":
      shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
      break;
    case "twitter":
      shareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
      break;
    case "facebook":
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
      break;
    case "link":
      navigator.clipboard.writeText(url);
      return;
    default:
      return;
  }

  if (shareUrl) {
    window.open(shareUrl, "_blank", "width=600,height=400");
  }
};

const SocialShare: React.FC<SocialShareProps> = ({
  url,
  title,
  variant = "desktop",
}) => {
  const handleShareClick = (platform: string) => {
    handleShare(platform, url, title);
  };

  if (variant === "mobile") {
    return (
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          gap: 1.5,
          mt: 4,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "#1e293b",
            fontWeight: 600,
          }}
        >
          Share article
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
          }}
        >
          <IconButton
            onClick={() => handleShareClick("linkedin")}
            sx={{
              width: 40,
              height: 40,
              bgcolor: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              color: "#0077b5",
              "&:hover": {
                bgcolor: "#f0f8ff",
                border: "1px solid #0077b5",
              },
            }}
          >
            <LinkedInIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            onClick={() => handleShareClick("twitter")}
            sx={{
              width: 40,
              height: 40,
              bgcolor: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              color: "#1DA1F2",
              "&:hover": {
                bgcolor: "#E8F5FE",
                border: "1px solid #1DA1F2",
              },
            }}
          >
            <TwitterIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            onClick={() => handleShareClick("facebook")}
            sx={{
              width: 40,
              height: 40,
              bgcolor: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              color: "#1877f2",
              "&:hover": {
                bgcolor: "#e8f0fe",
                border: "1px solid #1877f2",
              },
            }}
          >
            <FacebookIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            onClick={() => handleShareClick("link")}
            sx={{
              width: 40,
              height: 40,
              bgcolor: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              color: "#6366f1",
              "&:hover": {
                bgcolor: "#f3f4f6",
                border: "1px solid #6366f1",
              },
            }}
          >
            <LinkIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: { xs: "relative", md: "sticky" },
        top: { md: 100 },
        height: "fit-content",
        alignSelf: "flex-start",
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        minWidth: 60,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <IconButton
          onClick={() => handleShareClick("linkedin")}
          sx={{
            width: 40,
            height: 40,
            bgcolor: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            color: "#0077b5",
            "&:hover": {
              bgcolor: "#f0f8ff",
              border: "1px solid #0077b5",
            },
          }}
        >
          <LinkedInIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <IconButton
          onClick={() => handleShareClick("twitter")}
          sx={{
            width: 40,
            height: 40,
            bgcolor: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            color: "#1DA1F2",
            "&:hover": {
              bgcolor: "#E8F5FE",
              border: "1px solid #1DA1F2",
            },
          }}
        >
          <TwitterIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <IconButton
          onClick={() => handleShareClick("facebook")}
          sx={{
            width: 40,
            height: 40,
            bgcolor: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            color: "#1877f2",
            "&:hover": {
              bgcolor: "#e8f0fe",
              border: "1px solid #1877f2",
            },
          }}
        >
          <FacebookIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <IconButton
          onClick={() => handleShareClick("link")}
          sx={{
            width: 40,
            height: 40,
            bgcolor: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            color: "#6366f1",
            "&:hover": {
              bgcolor: "#f3f4f6",
              border: "1px solid #6366f1",
            },
          }}
        >
          <LinkIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: "#1e293b",
          fontWeight: 600,
          writingMode: "vertical-rl",
          textOrientation: "mixed",
          transform: "rotate(180deg)",
          mb: 2,
        }}
      >
        Share article
      </Typography>
    </Box>
  );
};

export default SocialShare;
